import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Loader2, Power, PowerOff } from 'lucide-react';
import { swatidService } from '@/services';
import type { ApiSwatidRelay } from '@/types/api';
import { useSwatidRefresh } from '@/hooks/useSwatidController';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface SwatidRelayToggleButtonProps {
  controllerId: string;
  relay: ApiSwatidRelay;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'default';
  fullWidth?: boolean;
}

function buildOnAction(relay: ApiSwatidRelay): { action: 'on'; duration?: number } {
  if (relay.mode === 'temporizado' && relay.defaultDuration > 0) {
    return { action: 'on', duration: relay.defaultDuration };
  }
  return { action: 'on' };
}

export function SwatidRelayToggleButton({
  controllerId,
  relay,
  disabled = false,
  className,
  size = 'sm',
  fullWidth = true,
}: SwatidRelayToggleButtonProps) {
  const { toast } = useToast();
  const { refreshAll } = useSwatidRefresh(controllerId);

  const mutation = useMutation({
    mutationFn: async () => {
      const params = relay.currentState
        ? { action: 'off' as const }
        : buildOnAction(relay);
      const res = await swatidService.controlRelay(controllerId, relay.relayNumber, params);
      if (!res.success) throw new Error(res.error?.message || 'Error al controlar relé');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      toast({
        title: relay.currentState ? 'Relé apagado' : 'Relé encendido',
        description: `Relé ${relay.relayNumber}: comando enviado.`,
      });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const isOn = relay.currentState;
  const busy = mutation.isPending;

  return (
    <Button
      type="button"
      size={size}
      variant={isOn ? 'outline' : 'default'}
      disabled={disabled || busy}
      className={cn(
        fullWidth && 'w-full',
        !isOn && 'bg-success hover:bg-success/90',
        isOn && 'border-destructive/50 text-destructive hover:bg-destructive/10',
        className
      )}
      onClick={(e) => {
        e.stopPropagation();
        mutation.mutate();
      }}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : isOn ? (
        <>
          <PowerOff className="w-3.5 h-3.5 mr-1" />
          Apagar
        </>
      ) : (
        <>
          <Power className="w-3.5 h-3.5 mr-1" />
          Encender
        </>
      )}
    </Button>
  );
}
